import { useMemo } from 'react';
import { paramCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';

import { LinearProgress } from '@app/ui/progress';
import { useIsMobile } from '@app/app/hooks/useIsMobile';
import { calculateSurveyProgress, isVisible } from '@app/dynamic/utils';

import { getProgressStatusConfig } from '@app/shared/utils';
import { FinishAndSubmit } from '@app/shared/components/buttons/FinishAndSubmit';
import { applicationConfig } from '../../configs';
import { Application } from '../../types/Application';
import { EDITABLE_APPLICATION_STATUSES } from '../../constants';
import { SurveyItem } from './SurveyItem';

export interface ApplicationListProps {
  readonly item: Application;
  readonly onClick: (survey: string) => void;
  readonly onSubmit?: () => void;
  readonly isAdmin?: boolean;
}

export const ApplicationList = ({ onClick, item, isAdmin, onSubmit }: ApplicationListProps) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();

  const applicationProgress = useMemo(() => calculateSurveyProgress(applicationConfig.surveys, item), [item]);

  const surveysConfig = useMemo(() => applicationConfig.surveys.filter(survey => isVisible(survey, item)), [item]);

  const notFinished = applicationProgress.progressTotal !== 100;

  const isSubmittedSurrogate = !EDITABLE_APPLICATION_STATUSES.includes(item.status) && !isAdmin;

  return (
    <>
      <Box
        sx={{
          display: {
            sm: 'flex',
          },
          alignItems: 'center',
          mb: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant={isMobile ? 'h2' : 'h3'}
          component="h3"
          sx={{
            whiteSpace: 'nowrap',
            mr: {
              sm: 3,
              md: 5,
            },
            mb: {
              xs: 1,
              sm: 0,
            },
          }}
        >
          {t('user.page.single.currentProgress')}
        </Typography>
        <LinearProgress value={applicationProgress.progressTotal} />
      </Box>
      <Paper
        sx={{
          borderRadius: '20px',
        }}
        variant="outlined"
        elevation={0}
      >
        {surveysConfig.map(survey => {
          const progressConfig = applicationProgress.populatedSurveyCountMap[survey.name];

          const { status, icon, iconButton, color } = getProgressStatusConfig(
            progressConfig.populatedPageCount,
            progressConfig.pageCount,
          );
          const handleSurvey = () => onClick(paramCase(survey.name));

          return (
            <SurveyItem
              key={survey.name}
              surveyLabel={t(`application.field.survey.option.${survey.name}.name`)}
              progressConfig={progressConfig}
              statusLabel={t(`application.field.form.status.${status}`)}
              color={color}
              icon={icon}
              iconButton={iconButton}
              isAdmin={isAdmin || false}
              isSubmitted={isSubmittedSurrogate}
              isMobile={isMobile}
              onClick={handleSurvey}
            />
          );
        })}
      </Paper>

      {!isAdmin && EDITABLE_APPLICATION_STATUSES.includes(item.status) && (
        <FinishAndSubmit
          onSubmit={onSubmit}
          notFinished={notFinished}
          tooltipText={t('application.button.finishAndSubmit.description')}
          buttonText={t('application.button.finishAndSubmit.name')}
        />
      )}
    </>
  );
};
